import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  IconButton,
  Box,
  Stack,
  Switch,
  Divider,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useGetAutoFillVariables } from '@/api/hooks/custom-payments/useGetAutoFillVariables';

const EMPTY_LABEL = { en: '', fr: '', es: '' };
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
];

const AutoFillSettingsModal = ({
  open,
  onClose,
  fieldTitle,
  value,
  formProperties,
  onSave,
}) => {
  const [type, setType] = useState('none');
  const [defaultValue, setDefaultValue] = useState('');
  const [options, setOptions] = useState([
    { value: '', label: { ...EMPTY_LABEL } },
  ]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [variable, setVariable] = useState('');
  const [isRequired, setIsRequired] = useState(false);

  const {
    variables,
    loading: variablesLoading,
    error: variablesError,
  } = useGetAutoFillVariables(open && type === 'variable');

  useEffect(() => {
    if (!open) return;
    const emptyOptions = [{ value: '', label: { ...EMPTY_LABEL } }];
    if (value?.type === 'options') {
      setType('options');
      setOptions(
        value.options?.length
          ? value.options.map((opt) => ({
              ...opt,
              label: { ...EMPTY_LABEL, ...opt.label },
            }))
          : emptyOptions,
      );
      setDefaultValue('');
      setVariable('');
    } else if (value?.type === 'default') {
      setType('default');
      setDefaultValue(value.defaultValue ?? '');
      setOptions(emptyOptions);
      setVariable('');
    } else if (value?.type === 'variable') {
      setType('variable');
      setVariable(value.variable ?? '');
      setDefaultValue('');
      setOptions(emptyOptions);
    } else {
      setType('none');
      setDefaultValue('');
      setOptions(emptyOptions);
      setVariable('');
    }
    setIsRequired(Boolean(formProperties?.isRequired));
  }, [open, value, formProperties]);

  const handleOptionLabelChange = (index, lang, newLabel) => {
    setOptions((prev) =>
      prev.map((opt, i) =>
        i === index
          ? { ...opt, label: { ...opt.label, [lang]: newLabel } }
          : opt,
      ),
    );
  };

  const handleOptionValueChange = (index, newValue) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, value: newValue } : opt)),
    );
  };

  const handleAddOption = () => {
    setOptions((prev) => [...prev, { value: '', label: { ...EMPTY_LABEL } }]);
  };

  const handleRemoveOption = (index) => {
    setOptions((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ value: '', label: { ...EMPTY_LABEL } }];
    });
  };

  const handleSave = () => {
    let nextAutoFill;
    if (type === 'none') {
      nextAutoFill = undefined;
    } else if (type === 'default') {
      const trimmed = defaultValue.trim();
      nextAutoFill =
        trimmed === '' ? undefined : { type: 'default', defaultValue };
    } else if (type === 'variable') {
      nextAutoFill = variable ? { type: 'variable', variable } : undefined;
    } else {
      const seen = new Set();
      const filtered = options
        .map((o) => ({ ...o, value: o.value.trim() }))
        .filter((o) => {
          if (!o.value || seen.has(o.value)) return false;
          seen.add(o.value);
          return true;
        });

      nextAutoFill =
        filtered.length === 0
          ? undefined
          : { type: 'options', options: filtered };
    }
    const nextFormProperties = isRequired ? { isRequired: true } : undefined;
    onSave({ autoFill: nextAutoFill, formProperties: nextFormProperties });
  };

  const handleClear = () => {
    onSave({ autoFill: undefined, formProperties: undefined });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        Field Settings
        {fieldTitle && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {fieldTitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <FormControlLabel
          control={
            <Switch
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
            />
          }
          label='Required'
        />
        <Divider sx={{ my: 2 }} />
        <FormControl>
          <FormLabel>Auto-fill type</FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value)}>
            <FormControlLabel value='none' control={<Radio />} label='None' />
            <FormControlLabel
              value='default'
              control={<Radio />}
              label='Default'
            />
            <FormControlLabel
              value='options'
              control={<Radio />}
              label='Options'
            />
            <FormControlLabel
              value='variable'
              control={<Radio />}
              label='Variable'
            />
          </RadioGroup>
        </FormControl>

        {type !== 'none' && (
          <Box sx={{ mt: 2 }}>
            {type === 'default' ? (
              <TextField
                fullWidth
                size='small'
                label='Default value'
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
              />
            ) : type === 'variable' ? (
              <FormControl
                fullWidth
                size='small'
                error={Boolean(variablesError)}>
                <InputLabel id='autofill-variable-label'>Variable</InputLabel>
                <Select
                  labelId='autofill-variable-label'
                  label='Variable'
                  value={variable}
                  onChange={(e) => setVariable(e.target.value)}
                  disabled={variablesLoading}>
                  {variable && !variables.some((v) => v.key === variable) && (
                    <MenuItem value={variable}>{variable}</MenuItem>
                  )}
                  {variables.map((v) => (
                    <MenuItem key={v.key} value={v.key}>
                      {v.label}
                    </MenuItem>
                  ))}
                </Select>
                {variablesLoading && (
                  <FormHelperText>Loading variables…</FormHelperText>
                )}
                {variablesError && (
                  <FormHelperText>{variablesError}</FormHelperText>
                )}
              </FormControl>
            ) : (
              <Stack spacing={2}>
                <FormControl size='small' sx={{ maxWidth: 200 }}>
                  <InputLabel id='options-language-label'>
                    Label language
                  </InputLabel>
                  <Select
                    labelId='options-language-label'
                    label='Label language'
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}>
                    {LANGUAGES.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack spacing={1}>
                  {options.map((opt, index) => (
                    <Box key={index}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          sx={{ flex: 1 }}
                          size='small'
                          label='Value'
                          value={opt.value}
                          onChange={(e) =>
                            handleOptionValueChange(index, e.target.value)
                          }
                        />
                        <TextField
                          sx={{ flex: 1 }}
                          size='small'
                          label={`Label (${
                            LANGUAGES.find((l) => l.code === selectedLanguage)
                              ?.name
                          })`}
                          value={opt.label[selectedLanguage]}
                          onChange={(e) =>
                            handleOptionLabelChange(
                              index,
                              selectedLanguage,
                              e.target.value,
                            )
                          }
                        />
                        <IconButton
                          aria-label='Remove option'
                          size='small'
                          onClick={() => handleRemoveOption(index)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                  <Box>
                    <Button
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={handleAddOption}>
                      Add option
                    </Button>
                  </Box>
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button
          color='error'
          onClick={handleClear}
          disabled={!value && !formProperties}>
          Clear settings
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ color: '#fff' }}>
            OK
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AutoFillSettingsModal;
